/** Net ROI Calculator body — Flip / BRRRR / Margin, Hard Money vs Cash, cost
 * breakdown. v1 permits-roi.js roiPanel math via math.ts. Shared by the
 * standalone RoiPage and the property-detail drawer's "Net ROI" tab.
 * `seed` supplies ARV / Purchase / Rehab from the lead; user edits (`over`) win.
 *
 * Per-lead persistence (NR_BY) is v2's now — it used to be v1-only, which meant
 * v2 could read an override but never create one. Pass `address` + `onSaveOver`
 * to enable Save; without them the calculator is scratch-only, as before. */
import { useEffect, useMemo, useState } from "react";
import { brrrResult, flipResult, NR_DEFAULTS, roiTier, usd, type NrInputs } from "./math";

const FLIP_INPUTS: { key: keyof NrInputs; label: string; suf: "$" | "%" | "" }[] = [
  { key: "arv", label: "ARV", suf: "$" },
  { key: "purchase", label: "Purchase Price", suf: "$" },
  { key: "rehab", label: "Rehab Cost", suf: "$" },
  { key: "hold", label: "Hold Days", suf: "" },
];
const LOAN_INPUTS: { key: keyof NrInputs; label: string; suf: "$" | "%" | "" }[] = [
  { key: "ltv", label: "LTV %", suf: "%" },
  { key: "rate", label: "Interest Rate", suf: "%" },
  { key: "pts", label: "Origination Points", suf: "%" },
  { key: "svc", label: "Loan Service Fee", suf: "$" },
];
const COST_INPUTS: { key: keyof NrInputs; label: string; suf: "$" | "%" | "" }[] = [
  { key: "kiwi", label: "Kiwi Buying Costs %", suf: "%" },
  { key: "buy", label: "Buying Costs %", suf: "%" },
  { key: "sell", label: "Selling Costs %", suf: "%" },
  { key: "insm", label: "Insurance (months)", suf: "" },
  { key: "insmo", label: "Insurance $/mo", suf: "$" },
  { key: "utilm", label: "Utilities (months)", suf: "" },
  { key: "utilmo", label: "Utilities $/mo", suf: "$" },
  { key: "staging", label: "Staging", suf: "$" },
];
const RENT_INPUTS: { key: keyof NrInputs; label: string; suf: "$" | "%" | "" }[] = [
  { key: "br_rent", label: "Monthly Rent", suf: "$" },
  { key: "br_vac", label: "Vacancy %", suf: "%" },
  { key: "br_mgmt", label: "Mgmt %", suf: "%" },
  { key: "br_maint", label: "Maintenance %", suf: "%" },
  { key: "br_capex", label: "CapEx %", suf: "%" },
  { key: "br_taxes", label: "Taxes / yr", suf: "$" },
  { key: "br_ins", label: "Insurance / yr", suf: "$" },
  { key: "br_hoa", label: "HOA / mo", suf: "$" },
];
const REFI_INPUTS: { key: keyof NrInputs; label: string; suf: "$" | "%" | "" }[] = [
  { key: "br_refiltv", label: "Refi LTV %", suf: "%" },
  { key: "br_refirate", label: "Refi Rate %", suf: "%" },
  { key: "br_refiterm", label: "Refi Term (yrs)", suf: "" },
];

const tierCls = (pct: number): string => "tier-" + roiTier(pct);
// NaN = undefined ratio (negative flow on $0 left in) — an em-dash, never a
// green "0.0%" (Tom 2026-08-11: "cash on cash doesnt work")
const pf = (x: number): string =>
  (x === Infinity ? "∞%" : Number.isNaN(x) ? "—" : x.toFixed(1) + "%");

export function RoiCalculator({ master, seed, address, onSaveOver, savedOver, onLive }: {
  master: NrInputs;
  seed: Partial<NrInputs>;
  /** the property these overrides belong to; omit for a scratch calculation */
  address?: string;
  /** persist to NR_BY[address]; omit to keep the calculator scratch-only */
  onSaveOver?: (address: string, vals: Record<string, number>) => void;
  /** what is already stored for this address, so Save starts clean */
  savedOver?: Record<string, number>;
  /** reports every recompute so the parent's Share/PDF summary prints EXACTLY
   * what this tab shows — saved or not (Tom 2026-08-19, Belgian Court: the
   * printout used org defaults while the tab held unsaved edits, so the two
   * disagreed on every number). */
  onLive?: (g: NrInputs) => void;
}) {
  const [tab, setTab] = useState<"flip" | "brrrr" | "margin">("flip");
  const [mode, setMode] = useState<"hard" | "cash">("hard");
  // Seed from what is already stored for this property, so opening the
  // calculator shows the override you saved rather than the org defaults.
  // Both render sites key the component by lead/deal, so this re-seeds on
  // switch without a reconciling effect.
  const [over, setOver] = useState<Partial<Record<keyof NrInputs, number>>>(
    () => ({ ...(savedOver as Partial<Record<keyof NrInputs, number>> | undefined) }));
  // What the box SHOWS, as typed. The numeric `over` above still drives every
  // calculation — this only exists so the input is not round-tripped through a
  // number while you are mid-keystroke.
  //
  // Without it: type "10." -> parseFloat gives 10 -> the box re-renders as "10"
  // -> the next keystroke appends -> "107". Every decimal field was unusable,
  // including the ones whose own defaults are decimals (rate 10.7, Kiwi 1.5,
  // selling costs 4.5), and clearing a field snapped it to 0. Settings > ROI
  // Defaults already keeps a string draft for exactly this reason.
  const [draft, setDraft] = useState<Partial<Record<keyof NrInputs, string>>>({});
  const [saved, setSaved] = useState(false);

  // v1 stored only the fields you actually overrode, keyed by raw address —
  // keep that shape so existing NR_BY entries and v2's readers (estMoney,
  // dealRehabValue, investor matching) keep working unchanged.
  const overVals = useMemo(() => {
    const out: Record<string, number> = {};
    for (const [k, v] of Object.entries(over)) {
      if (typeof v === "number" && Number.isFinite(v)) out[k] = v;
    }
    return out;
  }, [over]);
  const canSave = !!(address && onSaveOver);
  const dirty = useMemo(() => {
    const base = savedOver ?? {};
    const ks = new Set([...Object.keys(base), ...Object.keys(overVals)]);
    for (const k of ks) if (base[k] !== overVals[k]) return true;
    return false;
  }, [savedOver, overVals]);

  const saveOver = () => {
    if (!canSave || !dirty) return;
    onSaveOver(address, overVals);
    setSaved(true);
    setTimeout(() => setSaved(false), 2500);
  };

  const g: NrInputs = useMemo(
    () => ({ ...NR_DEFAULTS, ...master, ...seed, ...over }),
    [master, seed, over],
  );
  const f = useMemo(() => flipResult(g, mode), [g, mode]);
  const b = useMemo(() => brrrResult(g), [g]);
  useEffect(() => { onLive?.(g); }, [onLive, g]);

  const num = (key: keyof NrInputs, label: string, suf: string) => (
    <div key={key} className="arvfield">
      <label className="lbl">{label}{suf ? ` (${suf})` : ""}</label>
      <input
        className="moneyinput"
        inputMode="decimal"
        value={
          draft[key] !== undefined
            ? draft[key]
            : over[key] !== undefined ? String(over[key]) : g[key] ? String(g[key]) : ""
        }
        placeholder="0"
        onChange={(e) => {
          const s = e.target.value.replace(/[^0-9.\-]/g, "");
          const v = parseFloat(s);
          // Keep the typed string verbatim; commit the parsed number alongside
          // it. "10." and "" are legitimate mid-edit states that parse to
          // NaN — show them, and treat them as 0 for the maths only.
          setDraft((p) => ({ ...p, [key]: s }));
          setOver((p) => ({ ...p, [key]: Number.isFinite(v) ? v : 0 }));
        }}
      />
    </div>
  );

  const brk = (rows: [string, string, string?][]) => (
    <div className="brk">
      {rows.map(([l, v, cls]) => (
        <div key={l} className="brkrow">
          <span className={cls === "b" ? "brkblue" : "sub"}>{l}</span>
          <strong className={cls && cls !== "b" ? cls : ""}>{v}</strong>
        </div>
      ))}
    </div>
  );

  const margin = f.arv > 0 ? (f.profit / f.arv) * 100 : 0;
  const flipRec = f.roi >= 20 && f.profit > 0;

  return (
    <>
      {canSave ? (
        <div className="roisaverow">
          <span className="sub">
            {dirty
              ? "Unsaved changes — these numbers apply only to this property."
              : Object.keys(savedOver ?? {}).length
                ? "Saved for this property."
                : "Using the defaults from Settings → ROI Defaults."}
          </span>
          <button className="btn slim primary" onClick={saveOver} disabled={!dirty}>
            {saved ? "Saved ✓" : "Save for this property"}
          </button>
        </div>
      ) : null}
      <div className="roitabs">
        {(["flip", "brrrr", "margin"] as const).map((t) => (
          <button key={t} className={"vtab" + (tab === t ? " on" : "")} onClick={() => setTab(t)}>
            {t === "flip" ? "Flip" : t === "brrrr" ? "BRRRR" : "Margin"}
          </button>
        ))}
        {tab === "flip" ? (
          <span className="modeseg">
            <button className={"vtab" + (mode === "hard" ? " on" : "")} onClick={() => setMode("hard")}>Hard Money</button>
            <button className={"vtab" + (mode === "cash" ? " on" : "")} onClick={() => setMode("cash")}>Cash</button>
          </span>
        ) : null}
      </div>

      <div className="roibody">
        {tab === "flip" ? (
          <>
            <div className="roikpis">
              <div className="dkpi"><span className="lbl">Net profit</span><strong className={f.profit >= 0 ? "tier-good" : "tier-bad"}>{usd(f.profit)}</strong></div>
              <div className="dkpi"><span className="lbl">ROI %</span><strong className={tierCls(f.roi)}>{f.roi.toFixed(2)}%</strong></div>
              <div className="dkpi"><span className="lbl">Cash on cash</span><strong className={f.coc >= 0 ? "tier-good" : "tier-bad"}>{f.coc.toFixed(2)}%</strong></div>
              <div className="dkpi"><span className="lbl">Total costs</span><strong>{usd(f.total)}</strong></div>
              <div className="dkpi"><span className="lbl">Cash invested</span><strong>{usd(f.invested)}</strong></div>
            </div>
            <div className="roigrid">
              <div className="dcard">
                <div className="dhead"><h2>Deal Inputs</h2></div>
                <div className="secbody">{FLIP_INPUTS.map((i) => num(i.key, i.label, i.suf))}</div>
                {mode === "hard" ? (
                  <>
                    <div className="dhead"><h2>Loan Details</h2></div>
                    <div className="secbody">{LOAN_INPUTS.map((i) => num(i.key, i.label, i.suf))}</div>
                  </>
                ) : null}
                <div className="dhead"><h2>Costs</h2></div>
                <div className="secbody">{COST_INPUTS.map((i) => num(i.key, i.label, i.suf))}</div>
              </div>
              <div className="dcard">
                <div className="dhead"><h2>Cost Breakdown</h2></div>
                {brk([
                  ["Purchase Price", usd(f.purchase)],
                  ["Loan Amount", usd(f.loan), "b"],
                  ["Down Payment", usd(f.down)],
                  ["Interest Cost", usd(f.interest)],
                  ["Origination Fee", usd(f.orig)],
                  ["Loan Service Fee", usd(f.svc)],
                  ["Buying Costs", usd(f.buying)],
                  ["Rehab Cost", usd(f.rehab)],
                  ["Insurance", usd(f.insurance)],
                  ["Utilities", usd(f.utilities)],
                  ["Staging", usd(f.staging)],
                  ["Selling Costs", usd(f.selling)],
                  ["Total Costs", usd(f.total)],
                  ["ARV (Selling Price)", usd(f.arv)],
                  ["Net Profit", usd(f.profit), f.profit >= 0 ? "tier-good" : "tier-bad"],
                ])}
              </div>
            </div>
          </>
        ) : tab === "brrrr" ? (
          <>
            <div className="roikpis">
              <div className="dkpi"><span className="lbl">Monthly cash flow</span><strong className={b.cfMo >= 0 ? "tier-good" : "tier-bad"}>{usd(b.cfMo)}/mo</strong></div>
              <div className="dkpi"><span className="lbl">Cash-on-cash</span><strong className={Number.isNaN(b.coc) ? "tier-bad" : b.coc === Infinity || b.coc >= 0 ? "tier-good" : "tier-bad"}>{pf(b.coc)}</strong></div>
              <div className="dkpi"><span className="lbl">Cap rate</span><strong>{b.capRate.toFixed(1)}%</strong></div>
              <div className="dkpi"><span className="lbl">Cash left in deal</span><strong className={b.cashOut > 0 ? "tier-good" : ""}>{b.cashOut > 0 ? "+" + usd(b.cashOut) + " back" : usd(b.cashLeft)}</strong></div>
            </div>
            <div className="roigrid">
              <div className="dcard">
                <div className="dhead"><h2>Rental Inputs</h2></div>
                <div className="secbody">{RENT_INPUTS.map((i) => num(i.key, i.label, i.suf))}</div>
                <div className="dhead"><h2>Refinance</h2></div>
                <div className="secbody">{REFI_INPUTS.map((i) => num(i.key, i.label, i.suf))}</div>
                <p className="sub">Buy &amp; rehab numbers (ARV, Purchase, Rehab, buying/holding costs) are shared with the Flip tab.</p>
              </div>
              <div className="dcard">
                <div className="dhead"><h2>Refinance Breakdown</h2></div>
                {brk([
                  ["After Repair Value", usd(b.arv)],
                  ["Total Cash Invested", usd(b.totalCashIn)],
                  ["Refi Loan (cash-out)", usd(b.refiLoan), "b"],
                  ["Cash Left in Deal", b.cashOut > 0 ? "+" + usd(b.cashOut) + " back" : usd(b.cashLeft)],
                  ["Net Operating Income / yr", usd(b.noiY)],
                  ["Mortgage Payment", usd(b.mPmt) + "/mo"],
                  ["Forced Equity", usd(b.forcedEquity) + " · " + (b.arv > 0 ? ((b.forcedEquity / b.arv) * 100).toFixed(1) : 0) + "% of ARV", b.forcedEquity >= 0 ? "tier-good" : "tier-bad"],
                ])}
              </div>
            </div>
          </>
        ) : (
          <>
            <div className="cstats roimargin">
              <div className="cstat violet"><strong>{usd(f.purchase)}</strong><span>Purchase Price</span></div>
              <div className="cstat violet"><strong>{usd(f.rehab)}</strong><span>Rehab Estimate</span></div>
              <div className="cstat violet"><strong>{usd(f.arv)}</strong><span>After Repair Value</span></div>
              <div className="cstat violet"><strong>{f.arv > 0 ? (f.rehab / f.arv * 100).toFixed(1) : 0}%</strong><span>Reno % of ARV</span></div>
            </div>
            <div className={"recbox " + (flipRec ? "rec-flip" : "rec-brrrr")}>
              <strong>{flipRec ? "Fix & Flip Recommended" : "BRRRR Recommended"}</strong>
              <span className="sub">
                {flipRec
                  ? `${f.roi.toFixed(1)}% ROI with ${usd(f.profit)} profit — strong one-time return`
                  : `${pf(b.coc)} cash-on-cash with ${usd(b.cfMo)}/mo — long-term cash flow + equity`}
              </span>
            </div>
            <div className="roigrid">
              <div className={"dcard" + (flipRec ? " reco" : "")}>
                <div className="dhead"><h2>Fix &amp; Flip</h2>{flipRec ? <span className="lbadge bon">Recommended</span> : null}</div>
                {brk([
                  ["Total Investment", usd(f.total)],
                  ["Sale Price (ARV)", usd(f.arv)],
                  ["Net Profit", usd(f.profit), f.profit >= 0 ? "tier-good" : "tier-bad"],
                  ["ROI", f.roi.toFixed(1) + "%", tierCls(f.roi)],
                  ["Profit Margin", margin.toFixed(1) + "%", margin >= 0 ? "tier-good" : "tier-bad"],
                  ["Timeline", Math.max(Math.round(f.hold / 30), 1) + " months"],
                ])}
              </div>
              <div className={"dcard" + (!flipRec ? " reco" : "")}>
                <div className="dhead"><h2>BRRRR</h2>{!flipRec ? <span className="lbadge bon">Recommended</span> : null}</div>
                {brk([
                  ["Total Cash Invested", usd(b.totalCashIn)],
                  ["Forced Equity", usd(b.forcedEquity) + " · " + (b.arv > 0 ? ((b.forcedEquity / b.arv) * 100).toFixed(1) : 0) + "% of ARV", b.forcedEquity >= 0 ? "tier-good" : "tier-bad"],
                  ["Monthly Cash Flow", usd(b.cfMo) + "/mo", b.cfMo >= 0 ? "tier-good" : "tier-bad"],
                  ["Cash-on-Cash", pf(b.coc), Number.isNaN(b.coc) ? "tier-bad" : b.coc === Infinity || b.coc >= 0 ? "tier-good" : "tier-bad"],
                  ["Cap Rate", b.capRate.toFixed(1) + "%"],
                  ["Cash Left in Deal", b.cashOut > 0 ? "+" + usd(b.cashOut) + " cash back" : usd(b.cashLeft), b.cashOut > 0 ? "tier-good" : ""],
                ])}
              </div>
            </div>
          </>
        )}
      </div>
    </>
  );
}
